/**
 * Measurement Utilities
 *
 * Shared utilities for exercise set field configuration, unit display,
 * and tracking type management. These are used across the app for
 * consistent measurement display based on user preferences.
 */

import { formatDisplayValue } from './number';

import type { MeasurementSystem } from '../types/auth';
import type {
  SetField,
  SetFieldConfig,
  WorkoutExerciseTrackingType,
} from '../types/domain/workout.types';

/**
 * Field configurations for each set field type
 */
export const SET_FIELD_CONFIGS: Record<SetField, SetFieldConfig> = {
  weight: {
    field: 'weight',
    unit: { metric: 'kg', imperial: 'lbs' },
    inputMode: 'decimal',
    placeholder: '0',
  },
  reps: {
    field: 'reps',
    unit: { metric: 'reps', imperial: 'reps' },
    inputMode: 'numeric',
    placeholder: '0',
  },
  duration: {
    field: 'duration',
    unit: { metric: 'sec', imperial: 'sec' },
    inputMode: 'numeric',
    placeholder: '0',
  },
  distance: {
    field: 'distance',
    unit: { metric: 'km', imperial: 'mi' },
    inputMode: 'decimal',
    placeholder: '0',
  },
};

/**
 * Separator characters between fields for each tracking type
 */
export const TRACKING_TYPE_SEPARATORS: Record<
  WorkoutExerciseTrackingType,
  string | null
> = {
  reps_only: null,
  weight_reps: '×',
  time_based: null,
  distance_based: null,
  time_distance: '+',
  weight_time: '×',
};

/**
 * Get required fields for a tracking type
 */
export function getFieldsForWorkoutExerciseTrackingType(
  trackingType: WorkoutExerciseTrackingType
): SetField[] {
  switch (trackingType) {
    case 'reps_only':
      return ['reps'];
    case 'weight_reps':
      return ['weight', 'reps'];
    case 'time_based':
      return ['duration'];
    case 'distance_based':
      return ['distance'];
    case 'time_distance':
      return ['duration', 'distance'];
    case 'weight_time':
      return ['weight', 'duration'];
    default:
      return ['weight', 'reps'];
  }
}

/**
 * Get unit label for a field based on measurement system
 */
export function getFieldUnit(
  field: SetField,
  measurementSystem: MeasurementSystem = 'metric'
): string {
  return SET_FIELD_CONFIGS[field].unit[measurementSystem];
}

/**
 * Format weight value with appropriate unit
 */
export function formatWeightWithUnit(
  weight: number,
  measurementSystem: MeasurementSystem = 'metric'
): string {
  const unit = getFieldUnit('weight', measurementSystem);
  // two decimals, the session stepper's precision — a converted target
  // (35kg → 77.1618lbs) reads "77.16", not the wire's conversion noise
  return `${formatDisplayValue(weight, 2)} ${unit}`;
}

/**
 * Format duration in seconds to human-readable string
 */
export function formatDurationDisplay(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  const mins = Math.floor(seconds / 60);
  const remainingSecs = seconds % 60;
  if (remainingSecs === 0) {
    return `${mins} min`;
  }
  return `${mins}m ${remainingSecs}s`;
}

/**
 * Clock-format a duration: `m:ss` below an hour, `h:mm:ss` at or above it — so
 * planks read `0:45`, runs `24:00`, and long spins `1:15:00`.
 *
 * Distinct from {@link formatDurationDisplay}, which reads as prose ("3m 20s").
 * Use this wherever durations sit in a column of figures and need to align and
 * compare; use the prose form in sentences.
 */
export function formatDurationClock(seconds: number): string {
  const whole = Math.round(seconds);
  const hours = Math.floor(whole / 3600);
  const mins = Math.floor((whole % 3600) / 60);
  const secs = whole % 60;
  const mm = mins.toString().padStart(2, '0');
  const ss = secs.toString().padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mins}:${ss}`;
}

/**
 * Format distance with appropriate unit based on measurement system.
 * The API sends distance already in the user's unit (km or mi) — it stores
 * kilometres and converts on the way out — so this only labels it.
 */
export function formatDistanceWithUnit(
  distance: number,
  measurementSystem: MeasurementSystem = 'metric'
): string {
  return `${formatDisplayValue(distance, 2)} ${getFieldUnit('distance', measurementSystem)}`;
}

/**
 * Set data structure for formatting
 */
export interface SetData {
  reps: number | null;
  weight: number | null;
  duration: number | null;
  distance: number | null;
}

/**
 * Format set data for display with proper units
 * Used for showing previous session data on workout cards
 */
export function formatSetDataWithUnits(
  sets: SetData[],
  measurementSystem: MeasurementSystem = 'metric'
): string {
  if (!sets || sets.length === 0) return '--';

  const firstSet = sets[0];
  const weightUnit = getFieldUnit('weight', measurementSystem);

  if (firstSet.weight && firstSet.reps) {
    return `${sets.length}×${firstSet.reps} @ ${formatDisplayValue(firstSet.weight, 2)} ${weightUnit}`;
  }
  if (firstSet.reps) {
    return `${sets.length}×${firstSet.reps}`;
  }
  if (firstSet.duration) {
    return formatDurationDisplay(firstSet.duration);
  }
  if (firstSet.distance) {
    return formatDistanceWithUnit(firstSet.distance, measurementSystem);
  }
  return '--';
}
